import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Button, Input, Divider } from 'antd';
import AuthShell from './AuthShell';
import { SlackLogo, GoogleLogo } from './logos';
import { requestEmailOtp, goToGoogleLogin, goToSlackLogin } from './authApi';
import { isValidEmail, emailLengthError } from './emailValidation';

// Enjo-style "Sign in to your account": email -> OTP, plus Slack / Google.
const TrooprSignIn = (props) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Length problems are shown while typing; anything else waits for submit so
  // a half-typed address isn't called invalid. The live one wins when both
  // exist — it is the more specific, and the actionable, message.
  const lengthError = emailLengthError(email);
  const shownError = lengthError || error;

  const submit = async () => {
    setError('');
    if (lengthError) return;
    if (!isValidEmail(email)) {
      return setError('Please enter a valid email address');
    }
    setLoading(true);
    try {
      const res = await requestEmailOtp(email.trim(), { isSignup: false });
      setLoading(false);
      if (res.data && res.data.success) {
        props.history.push({
          pathname: '/verification',
          state: { sessionId: res.data.sessionId, email: email.trim(), isSignup: false },
        });
      } else {
        setError((res.data && res.data.message) || 'Could not send code. Try again.');
      }
    } catch (e) {
      setLoading(false);
      // Surface a server-supplied reason (e.g. a rejected address) instead of
      // the generic retry copy, which tells the user to retry something that
      // can never succeed.
      const msg = e && e.response && e.response.data && e.response.data.message;
      setError(msg || 'Something went wrong. Please try again.');
    }
  };

  return (
    <AuthShell>
      <h1 className="tpr-auth__title">Sign in to your account</h1>
      <p className="tpr-auth__subtitle">Use your work email to easily collaborate</p>

      <div className="tpr-auth__field">
        <Input
          className="tpr-auth__input"
          type="email"
          name="email"
          id="email"
          autoComplete="email"
          placeholder="name@work-email.com"
          aria-invalid={shownError ? 'true' : undefined}
          aria-describedby={shownError ? 'email-error' : undefined}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            // Drop a stale server/submit error the moment they start fixing it.
            if (error) setError('');
          }}
          onPressEnter={submit}
        />
        {/* Below the input, and derived from the current value rather than
            stored — so it clears itself the moment the address fits again. */}
        {shownError && (
          <span className="tpr-auth__error" id="email-error" role="alert">
            * {shownError}
          </span>
        )}
      </div>

      <Button
        className="tpr-auth__primary"
        type="primary"
        loading={loading}
        disabled={!!lengthError}
        onClick={submit}
      >
        Sign in with email
      </Button>

      <Divider className="tpr-auth__divider">OR</Divider>

      <div className="tpr-auth__providers">
        <Button className="tpr-auth__provider" onClick={goToSlackLogin}>
          <SlackLogo /> Continue with Slack
        </Button>
        <Button className="tpr-auth__provider" onClick={goToGoogleLogin}>
          <GoogleLogo /> Continue with Google
        </Button>
      </div>

      <div className="tpr-auth__footer">
        New to Troopr?{' '}
        <span
          className="tpr-auth__link"
          onClick={() => props.history.push('/signup')}
        >
          Create an account
        </span>
      </div>
    </AuthShell>
  );
};

export default withRouter(TrooprSignIn);
